<!-- Vue SFC -->
<template>
    <div>
        <treeselect ref="treeselect-base" v-model="selected_value" :options="options_data" :closeOnSelect="true"
            :disable-branch-nodes="true" :multiple="multiple" :async="!!route" :load-options="loadOptions"
            :auto-load-root-options="false" :cache-options="false" :loading="isLoading" :show-count-of="'ALL_CHILDREN'"
            :show-count-on-search="true" :default-expand-level="0" :always-open="false" :append-to-body="true"
            :z-index="9999" v-on:select="(node) => sendNode(node)" v-on:input="(node) => clearValue(node)"
            v-on:search-change="handleSearchChange" v-on:open="openSelect" :flatten-search-results="false" :name="name"
            :id="name" placeholder="กรุณาเลือก..." no-options-text="ไม่พบข้อมูล" no-results-text="ไม่พบผลลัพธ์"
            search-prompt-text="พิมพ์เพื่อค้นหา..." loading-text="กำลังโหลด...">
            <label slot="option-label" slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }"
                :class="labelClassName" @click.stop="onExpandClick(node)">
                {{ node.label }}
                <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span>
            </label>
        </treeselect>
        <!-- Debug information (remove in production) -->
        <!-- <div v-if="$root.debug" class="mt-2 text-muted small">
            <strong>Debug Info:</strong><br>
            Options count: {{ options_data.length }}<br>
            Selected: {{ selected_value }}<br>
            Async mode: {{ !!route }}<br>
            Route: {{ route }}<br>
            Raw options count: {{ options ? options.length : 0 }}
        </div> -->
    </div>
</template>

<script>
// import the component
import Treeselect from "@riophae/vue-treeselect";
// import the styles
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
// import helper functions
import {
    convertFlatToHierarchical,
    convertLevelToHierarchical,
    addVisualCues,
} from '../helpers/TreeselectHelper.js';

export default {
    // register the component
    props: ["name", "options", "value", "route", "multiple"],
    components: { Treeselect },
    data() {
        return {
            options_data: [],
            selected_value: this.value,
            isLoading: false,
            searchTimeout: null,
        };
    },
    watch: {
        value(_new, old) {
            this.selected_value = _new;
        },
        options: {
            handler(newOptions) {
                // Only update options_data if not in async mode (no route)
                try {
                    if (!this.route && newOptions && newOptions.length > 0) {
                        console.log('Options changed:', newOptions);
                        // Process options to add proper indentation and hierarchy
                        const processedOptions = this.processOptionsForTreeselect(newOptions);
                        this.options_data = processedOptions;
                        console.log('Options processed and set:', processedOptions);
                    } else if (!this.route && (!newOptions || newOptions.length === 0)) {
                        // Clear options if empty
                        this.options_data = [];
                        console.log('Options cleared');
                    }
                } catch (error) {
                    console.error('Error processing options:', error);
                    this.options_data = [];
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        // Initialize options if provided and not in async mode
        try {
            if (!this.route && this.options && this.options.length > 0) {
                console.log('Initializing options in mounted:', this.options);
                this.options_data = this.processOptionsForTreeselect(this.options);
                console.log('Options initialized:', this.options_data);
            }
        } catch (error) {
            console.error('Error initializing options in mounted:', error);
            this.options_data = [];
        }
    },
    beforeDestroy() {
        // Clear timeout to prevent memory leaks
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    },
    methods: {
        onExpandClick(node) {
            if (node.level == 0 && node.isExpanded == true) {
                const leaves = this.getLeafNodes(node);

                if (leaves.length === 1) {
                    this.$refs['treeselect-base'].closeMenu();
                    this.$refs['treeselect-base'].blurInput();
                    node.isExpanded = false;
                    this.selected_value = leaves[0].id;

                    // Emit the tree-select event so parent component receives the selection
                    // Find the full node from the forest to include all data
                    const treeselect = this.$refs['treeselect-base'];
                    const leafNode = treeselect.forest.nodeMap[leaves[0].id];
                    if (leafNode) {
                        this.$emit("tree-select", leafNode);
                    } else {
                        // Fallback: emit with basic info
                        this.$emit("tree-select", { id: leaves[0].id, label: leaves[0].label });
                    }
                    this.sendNode(leaves[0]);
                }
                return false; // ถ้ามีหลาย leaf → return false
            }
        },
        getLeafNodes(node) {
            if (!node.children || node.children.length === 0) {
                return [{
                    id: node.id,
                    label: node.label,
                    level: node.level,
                    raw: node.raw || node
                }];
            }
            return node.children.flatMap(child => this.getLeafNodes(child));
        },
        openSelect() {
            let _this = this;
            setTimeout(() => {
                _this.$emit("open");
            }, 0);
        },
        // Handle search change for both async and non-async modes
        handleSearchChange(searchQuery) {
            let _this = this;
            setTimeout(() => {
                _this.$emit("search-change", searchQuery);
            }, 300);
        },
        // Debounced search function (for non-async fallback)
        debouncedFetchOptions(searchQuery) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.fetchOptions(searchQuery);
            }, 300); // Wait 300ms after user stops typing
        },
        async fetchOptions(searchQuery) {
            if (this.route) {
                this.isLoading = true;
                try {
                    let res = await axios.get(this.route, {
                        params: {
                            search: searchQuery,
                        },
                    });

                    console.log('API Response:', res.data); // Debug log

                    if (res.data.success && res.data.result) {
                        let options = res.data.result.map((item) => {
                            return this.formatOptionWithIndentation(item);
                        });

                        console.log('Mapped options:', options); // Debug log

                        // Use Vue.set to ensure reactivity
                        this.$set(this, 'options_data', options);

                        // Alternative: Use nextTick for better DOM update timing
                        this.$nextTick(() => {
                            this.$forceUpdate();
                        });
                    } else {
                        console.warn('API response format issue:', res.data);
                        this.options_data = [];
                    }
                } catch (error) {
                    console.error('Error fetching options:', error);
                    this.options_data = [];
                } finally {
                    this.isLoading = false;
                }
            }
        },
        async loadOptions({ action, searchQuery, parentNode, callback }) {
            console.log('LoadOptions called:', { action, searchQuery });

            try {
                if (action === 'ASYNC_SEARCH' && this.route) {
                    const res = await axios.get(this.route, {
                        params: {
                            search: searchQuery || '',
                        },
                    });

                    console.log('LoadOptions API Response:', res.data);

                    if (res.data.success && res.data.result) {
                        let options = res.data.result.map((item) => {
                            return this.formatOptionWithIndentation(item);
                        });

                        console.log('LoadOptions Mapped options:', options);

                        // For async mode, we need to replace the entire options array
                        this.$set(this, 'options_data', options);

                        callback(null, options);
                    } else {
                        console.warn('LoadOptions API response format issue:', res.data);
                        this.options_data = [];
                        callback(null, []);
                    }
                } else {
                    callback();
                }
            } catch (error) {
                console.error('Error in loadOptions:', error);
                this.options_data = [];
                callback(error);
            }
        },
        // Format option with proper indentation and hierarchy
        formatOptionWithIndentation(item, level = 0) {
            if (!item) return null;

            const indentSymbol = "  ".repeat(level); // 2 spaces per level
            const hasChildren = item.children && item.children.length > 0;

            // Support multiple field names for text
            const text = item.text || item.label || item.name || item.title || 'Unnamed';
            // Support multiple field names for id
            const id = item.id || item.value || item.key;

            console.log('Formatting item:', item, 'at level:', level, 'text:', text);

            let formattedOption = {
                id: id,
                label: `${indentSymbol}${text}`,
                level: level,
                isLeaf: !hasChildren,
                raw: item, // Keep original data
            };

            // If item has children, add them recursively
            if (hasChildren) {
                formattedOption.children = item.children
                    .map(child => this.formatOptionWithIndentation(child, level + 1))
                    .filter(child => child !== null); // Filter out null values
            }

            return formattedOption;
        },
        // Process options for treeselect with proper formatting
        processOptionsForTreeselect(items) {
            if (!Array.isArray(items)) return [];

            console.log('Processing options:', items);

            // ตรวจสอบว่าข้อมูลมีโครงสร้างแบบไหน
            const hasParentId = items.some(item => item.parent_id !== undefined);
            const hasLevel = items.some(item => item.level !== undefined);
            const hasChildren = items.some(item => item.children && Array.isArray(item.children));

            let formattedOptions = [];

            if (hasChildren) {
                // ข้อมูลมี children อยู่แล้ว ใช้ formatOptionWithIndentation โดยตรง
                formattedOptions = items.map(item => this.formatOptionWithIndentation(item, 0))
                    .filter(item => item !== null);
            } else if (hasParentId && typeof convertFlatToHierarchical === 'function') {
                // แปลงจาก flat data ที่มี parent_id
                const hierarchicalData = convertFlatToHierarchical(items, 'parent_id', 'id', 'text');
                formattedOptions = hierarchicalData.map(item => this.formatOptionWithIndentation(item, 0))
                    .filter(item => item !== null);
            } else if (hasLevel && typeof convertLevelToHierarchical === 'function') {
                // แปลงจาก level-based data
                const hierarchicalData = convertLevelToHierarchical(items, 'level', 'id', 'text');
                formattedOptions = hierarchicalData.map(item => this.formatOptionWithIndentation(item, 0))
                    .filter(item => item !== null);
            } else {
                // ข้อมูลแบบ flat ธรรมดา แปลงให้เป็น treeselect format
                formattedOptions = items.map(item => {
                    if (!item) return null;

                    return {
                        id: item.id || item.value || item.key,
                        label: item.text || item.label || item.name || item.title || 'Unnamed',
                        isLeaf: true,
                        raw: item
                    };
                }).filter(item => item !== null);
            }

            console.log('Formatted options:', formattedOptions);
            return formattedOptions;
        },
        // Process flat array into hierarchical structure with indentation
        processHierarchicalOptions(items) {
            // This method is deprecated - use processOptionsForTreeselect instead
            return this.processOptionsForTreeselect(items);
        },
        // Enhanced mapping for better visual hierarchy
        enhanceOptionsWithVisualCues(options) {
            return addVisualCues(options, {
                showIcons: true,
                showLines: true,
                branchIcon: '📁',
                leafIcon: '',
                indentSize: 2
            });
        },
        setValue(value) {
            this.selected_value = value;
        },
        // Get current options data for debugging
        getCurrentOptions() {
            return {
                raw_options: this.options,
                processed_options: this.options_data,
                selected_value: this.selected_value,
                is_async_mode: !!this.route
            };
        },
        // Debug method to check current state
        debugCurrentState() {
            console.log('Current Treeselect State:', {
                options: this.options,
                options_data: this.options_data,
                selected_value: this.selected_value,
                isLoading: this.isLoading,
                route: this.route,
                is_async_mode: !!this.route
            });
        },
        sendNode(node) {
            // Fix Error $refs
            let _this = this;
            setTimeout(() => {
                _this.$emit("tree-select", node);
            }, 0);
        },
        clearValue(node) {
            if (node == undefined) {
                let _this = this;
                setTimeout(() => {
                    _this.$emit("de-select", node);
                }, 0);
            }
        },
    },
};
</script>

<style scoped>
/* Enhanced styles for better tree visualization with clear indentation */
/* Override vue-treeselect.css with highest priority */

/* Base option styling - Remove indentation completely */
::v-deep .vue-treeselect__option {
    position: relative;
    padding: 0.5rem 1rem !important;
    border-bottom: 1px solid #f8f9fa;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-left: 1rem !important;
    /* Force same padding for all levels */
}


/* Override specific indent level classes from vue-treeselect.css */
::v-deep .vue-treeselect__indent-level-1 .vue-treeselect__option {
    padding-left: 10px !important;
    /* Level 1: 10px */
}

::v-deep .vue-treeselect__indent-level-2 .vue-treeselect__option {
    padding-left: 15px !important;
    /* Level 2: 15px */
}

::v-deep .vue-treeselect__indent-level-3 .vue-treeselect__option {
    padding-left: 20px !important;
    /* Level 3: 20px */
}

::v-deep .vue-treeselect__indent-level-4 .vue-treeselect__option {
    padding-left: 25px !important;
    /* Level 4: 25px */
}

::v-deep .vue-treeselect__indent-level-5 .vue-treeselect__option {
    padding-left: 30px !important;
    /* Level 5: 30px */
}

/* More specific override for all possible combinations */
::v-deep .vue-treeselect__list .vue-treeselect__indent-level-1 .vue-treeselect__option {
    padding-left: 10px !important;
}

::v-deep .vue-treeselect__list .vue-treeselect__indent-level-2 .vue-treeselect__option {
    padding-left: 15px !important;
}

::v-deep .vue-treeselect__list .vue-treeselect__indent-level-3 .vue-treeselect__option {
    padding-left: 20px !important;
}

::v-deep .vue-treeselect__list .vue-treeselect__indent-level-4 .vue-treeselect__option {
    padding-left: 25px !important;
}

::v-deep .vue-treeselect__list .vue-treeselect__indent-level-5 .vue-treeselect__option {
    padding-left: 30px !important;
}

/* Even more specific override */
::v-deep .vue-treeselect__menu .vue-treeselect__list .vue-treeselect__indent-level-1 .vue-treeselect__option {
    padding-left: 10px !important;
}

::v-deep .vue-treeselect__menu .vue-treeselect__list .vue-treeselect__indent-level-2 .vue-treeselect__option {
    padding-left: 15px !important;
}

::v-deep .vue-treeselect__menu .vue-treeselect__list .vue-treeselect__indent-level-3 .vue-treeselect__option {
    padding-left: 20px !important;
}

::v-deep .vue-treeselect__menu .vue-treeselect__list .vue-treeselect__indent-level-4 .vue-treeselect__option {
    padding-left: 25px !important;
}

::v-deep .vue-treeselect__menu .vue-treeselect__list .vue-treeselect__indent-level-5 .vue-treeselect__option {
    padding-left: 30px !important;
}


/* Level-based indentation - Override to remove indentation */
::v-deep .vue-treeselect__option[aria-level="1"],
::v-deep .vue-treeselect__option[aria-level="2"],
::v-deep .vue-treeselect__option[aria-level="3"],
::v-deep .vue-treeselect__option[aria-level="4"],
::v-deep .vue-treeselect__option[aria-level="5"] {
    padding-left: 1rem !important;
    border-left: none !important;
    background: #ffffff !important;
}

/* Remove alternative indentation approach */
::v-deep .vue-treeselect__option[aria-level]::before {
    display: none !important;
}

/* Branch (parent) node styling */
::v-deep .vue-treeselect__option.vue-treeselect__option--branch {
    font-weight: 600;
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
}

/* Leaf (child) node styling */
::v-deep .vue-treeselect__option.vue-treeselect__option--leaf {
    font-weight: 400;
    color: #495057;
}

/* Visual hierarchy with connecting lines */
::v-deep .vue-treeselect__option.vue-treeselect__option--leaf::after {
    content: "";
    position: absolute;
    left: 0.75rem;
    top: 0;
    bottom: 50%;
    width: 1px;
    background-color: #dee2e6;
}

/* Hover effects */
::v-deep .vue-treeselect__option:hover {
    background-color: #e3f2fd !important;
    color: #1976d2;
    transform: translateX(2px);
    transition: all 0.2s ease;
}

/* Selected option styling */
::v-deep .vue-treeselect__option.vue-treeselect__option--selected {
    background-color: #bbdefb !important;
    font-weight: 600;
    color: #0d47a1;
    border-left: 4px solid #1976d2;
}

/* Focused option styling */
::v-deep .vue-treeselect__option.vue-treeselect__option--focused {
    background-color: #e1f5fe !important;
    outline: 2px solid #03a9f4;
    outline-offset: -2px;
}

/* Disabled option styling */
::v-deep .vue-treeselect__option.vue-treeselect__option--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f8f9fa;
    color: #6c757d;
}

/* Expand/collapse arrow styling */
::v-deep .vue-treeselect__option-arrow-container {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

::v-deep .vue-treeselect__option-arrow {
    border-color: #6c757d transparent transparent transparent;
    border-width: 6px 4px 0 4px;
    transition: transform 0.2s ease;
}

/* Collapsed state arrow */
::v-deep .vue-treeselect__option--collapsed .vue-treeselect__option-arrow {
    transform: rotate(-90deg);
}

/* Expanded state arrow */
::v-deep .vue-treeselect__option--expanded .vue-treeselect__option-arrow {
    transform: rotate(0deg);
}

/* Better menu container styling */
::v-deep .vue-treeselect__menu {
    border-radius: 0.375rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border: 1px solid #dee2e6;
    max-height: 300px;
    overflow-y: auto;
}

/* Scrollbar styling */
::v-deep .vue-treeselect__menu::-webkit-scrollbar {
    width: 6px;
}

::v-deep .vue-treeselect__menu::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::v-deep .vue-treeselect__menu::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

::v-deep .vue-treeselect__menu::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Loading state styling */
::v-deep .vue-treeselect__loading-tip {
    padding: 1rem;
    text-align: center;
    color: #6c757d;
    font-style: italic;
    background-color: #f8f9fa;
}

/* No options text styling */
::v-deep .vue-treeselect__no-options-tip {
    padding: 1rem;
    text-align: center;
    color: #6c757d;
    font-style: italic;
    background-color: #f8f9fa;
}

/* Search highlighting */
::v-deep .vue-treeselect__option-label-container {
    flex: 1;
    overflow: hidden;
}

::v-deep .vue-treeselect__option-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Custom indent classes - Override to remove indentation */
.indent-level-0,
.indent-level-1,
.indent-level-2,
.indent-level-3,
.indent-level-4,
.indent-level-5 {
    padding-left: 1rem !important;
}

/* Responsive adjustments - Remove indentation */
@media (max-width: 576px) {
    ::v-deep .vue-treeselect__option {
        padding: 0.75rem 0.5rem !important;
        padding-left: 0.5rem !important;
    }

    .indent-level-1,
    .indent-level-2,
    .indent-level-3,
    .indent-level-4,
    .indent-level-5 {
        padding-left: 0.5rem !important;
    }
}

/* Print styles */
@media print {
    ::v-deep .vue-treeselect__option {
        background: none !important;
        color: #000 !important;
    }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    ::v-deep .vue-treeselect__option {
        border-bottom: 2px solid #000;
    }

    ::v-deep .vue-treeselect__option:hover {
        background-color: #000 !important;
        color: #fff !important;
    }
}

/* Accessibility improvements */
::v-deep .vue-treeselect__option:focus {
    outline: 2px solid #0066cc;
    outline-offset: -2px;
}

/* Animation for expand/collapse */
::v-deep .vue-treeselect__option-arrow {
    transition: transform 0.3s ease;
}

/* Subtle animation for option state changes */
::v-deep .vue-treeselect__option {
    transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
</style>

<!-- Global CSS to force override vue-treeselect indentation -->
<style>
/* Global override without scoped - Progressive indentation */
.vue-treeselect__indent-level-1 .vue-treeselect__option {
    padding-left: 10px !important;
    /* Base: 10px */
}

.vue-treeselect__indent-level-2 .vue-treeselect__option {
    padding-left: 15px !important;
    /* Base + 5px */
}

.vue-treeselect__indent-level-3 .vue-treeselect__option {
    padding-left: 20px !important;
    /* Base + 10px */
}

.vue-treeselect__indent-level-4 .vue-treeselect__option {
    padding-left: 25px !important;
    /* Base + 15px */
}

.vue-treeselect__indent-level-5 .vue-treeselect__option {
    padding-left: 30px !important;
    /* Base + 20px */
}

/* For deeper levels (if any) */
.vue-treeselect__indent-level-6 .vue-treeselect__option {
    padding-left: 35px !important;
    /* Base + 25px */
}

.vue-treeselect__indent-level-7 .vue-treeselect__option {
    padding-left: 40px !important;
    /* Base + 30px */
}

/* Alternative selectors for progressive indentation */
div[class*="vue-treeselect__indent-level-1"] .vue-treeselect__option {
    padding-left: 10px !important;
}

div[class*="vue-treeselect__indent-level-2"] .vue-treeselect__option {
    padding-left: 15px !important;
}

div[class*="vue-treeselect__indent-level-3"] .vue-treeselect__option {
    padding-left: 20px !important;
}

div[class*="vue-treeselect__indent-level-4"] .vue-treeselect__option {
    padding-left: 25px !important;
}

div[class*="vue-treeselect__indent-level-5"] .vue-treeselect__option {
    padding-left: 30px !important;
}
</style>
